// components/DiscoverySection.tsx
"use client";

import React, { forwardRef } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface DiscoverySectionProps {}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const DiscoverySection = forwardRef<
  HTMLDivElement, // ✅ the DOM node you expose
  DiscoverySectionProps // ✅ the props your component accepts
>(function DiscoverySection(_, ref) {
  const t = useTranslations("HomePage");

  return (
    <section ref={ref} className="relative py-12 mb-16">
      <Image
        src="/landing-discover-bg.jpg"
        alt="Discover Background"
        fill
        className="object-cover object-top absolute -z-10 opacity-45"
        priority
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.8 }}
        variants={containerVariants}
        className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16"
      >
        <motion.div variants={itemVariants} className="my-12 text-center">
          <h2 className="text-3xl font-semibold text-gray-800">
            {t("actions.discover")}
          </h2>
          <p className="mt-4 text-4xl font-bold text-black">
            {t("actions.tit")}
          </p>
          <p className="mt-4 text-base text-gray-500 max-w-3xl mx-auto">
            {t("actions.subTit")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 xl:gap-16">
          {[
            {
              imageSrc: "/landing-icon-wand.png",
              title: t("actions.searchProperties"),
              description: t("actions.browseDescription"),
            },
            {
              imageSrc: "/landing-icon-calendar.png",
              title: t("actions.bookRental"),
              description: t("actions.bookRentalDesc"),
            },
            {
              imageSrc: "/landing-icon-heart.png",
              title: t("actions.enjoyHome"),
              description: t("actions.enjoyHomeDesc"),
            },
          ].map((card, i) => (
            <motion.div key={i} variants={itemVariants}>
              <DiscoverCard {...card} />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
});

DiscoverySection.displayName = "DiscoverySection";
export default DiscoverySection;

type DiscoverCardProps = {
  imageSrc: string;
  title: string;
  description: string;
};

function DiscoverCard({ imageSrc, title, description }: DiscoverCardProps) {
  return (
    <div className="px-4 py-12 shadow-lg rounded-lg bg-primary-50 md:h-72">
      <div className="bg-primary-700 p-2 rounded-full mb-4 h-10 w-10 mx-auto">
        <Image src={imageSrc} width={30} height={30} alt={title} />
      </div>
      <h3 className="mt-4 text-xl text-center font-medium text-gray-800">
        {title}
      </h3>
      <p className="mt-2 text-center text-gray-500">{description}</p>
    </div>
  );
}
